// src/theme/index.ts
// The application face, plus the commented-out alternatives; see
// src/theme/foundations/typography.ts for when to reach for which.
// import '@fontsource-variable/newsreader'
// import '@fontsource-variable/newsreader/wght-italic.css'
// import '@fontsource-variable/host-grotesk'
// import '@fontsource-variable/tasa-orbiter'
// import '@fontsource-variable/atkinson-hyperlegible-next'
import '@fontsource-variable/geist'
import { createSystem, defineConfig, defaultConfig } from '@chakra-ui/react'
import { colorSemanticTokens, colorTokens } from './foundations/palettes'
import { semanticSizes, semanticSpacing } from './foundations/semantic-layout'
import { formLayoutRecipe } from './recipes/form-layout.recipe'
import { pageRecipe } from './recipes/page.recipe'
import { pageContainerRecipe } from './recipes/page-container.recipe'
import { semanticRadii } from './foundations/semantic-radii'
import { fontSizeTokens, fontTokens, textStyles } from './foundations/typography'
import {
  actionBarRadiusRecipe,
  alertRadiusRecipe,
  badgeRadiusRecipe,
  buttonRadiusRecipe,
  cardRadiusRecipe,
  comboboxRadiusRecipe,
  inputAddonRadiusRecipe,
  inputRadiusRecipe,
  nativeSelectRadiusRecipe,
  numberInputRadiusRecipe,
  pinInputRadiusRecipe,
  segmentGroupRadiusRecipe,
  selectRadiusRecipe,
  statRadiusRecipe,
  tabsRadiusRecipe,
  tagRadiusRecipe,
  tagsInputRadiusRecipe,
  textareaRadiusRecipe,
  toastRadiusRecipe,
} from './recipes/component-radius.recipe'
import { dashboardControlRecipe } from './recipes/dashboard-control.recipe'
import { dialogRecipe } from './recipes/dialog.recipe'
import { sibHeaderRecipe } from './recipes/sib-header.recipe'
import {
  drawerTypographyRecipe,
  emptyStateTypographyRecipe,
  headingTypographyRecipe,
} from './recipes/typography.recipe'

const config = defineConfig({
  globalCss: {
    // Indigo is the brand accent, so controls reach it without being told. An
    // explicit colorPalette belongs only where a call site departs from the
    // brand on purpose - green, red, orange for status, blue for information.
    ':root': {
      colorPalette: 'indigo',
    },
    body: {
      fontFamily: 'body',
    },
    // Both font tokens currently resolve to TASA Orbiter. The split is kept so the
    // Newsreader accent can be restored without touching call sites.
    'h1, h2': {
      fontFamily: 'heading',
    },
    'h3, h4, h5, h6': {
      fontFamily: 'body',
    },
    html: {
      overflowY: 'scroll',
      scrollbarGutter: 'stable',
    },
    '*': {
      scrollbarWidth: 'thin',
      scrollbarColor: '#bfc7d4 transparent',
      '&::-webkit-scrollbar': {
        width: '12px',
        height: '12px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
        border: 'none',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#bfc7d4',
        borderRadius: '999px',
        border: '0 solid transparent',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#aab4c3',
      },
    },
  },
  theme: {
    semanticTokens: {
      colors: colorSemanticTokens,
      sizes: semanticSizes,
      spacing: semanticSpacing,
      radii: semanticRadii,
    },
    tokens: {
      colors: colorTokens,
      fonts: fontTokens,
      fontSizes: fontSizeTokens,
    },
    textStyles,
    recipes: {
      badge: badgeRadiusRecipe,
      button: buttonRadiusRecipe,
      heading: headingTypographyRecipe,
      input: inputRadiusRecipe,
      pageContainer: pageContainerRecipe,
      sibHeader: sibHeaderRecipe,
      inputAddon: inputAddonRadiusRecipe,
      textarea: textareaRadiusRecipe,
    },
    slotRecipes: {
      actionBar: actionBarRadiusRecipe,
      alert: alertRadiusRecipe,
      card: cardRadiusRecipe,
      combobox: comboboxRadiusRecipe,
      dashboardControl: dashboardControlRecipe,
      dialog: dialogRecipe,
      formLayout: formLayoutRecipe,
      page: pageRecipe,
      drawer: drawerTypographyRecipe,
      emptyState: emptyStateTypographyRecipe,
      nativeSelect: nativeSelectRadiusRecipe,
      numberInput: numberInputRadiusRecipe,
      pinInput: pinInputRadiusRecipe,
      segmentGroup: segmentGroupRadiusRecipe,
      select: selectRadiusRecipe,
      stat: statRadiusRecipe,
      tabs: tabsRadiusRecipe,
      tag: tagRadiusRecipe,
      tagsInput: tagsInputRadiusRecipe,
      toast: toastRadiusRecipe,
    },
  },
})

export const system = createSystem(defaultConfig, config)
export type AppSystem = typeof system
